import { inject, Injectable } from '@angular/core';
import { Observable, forkJoin, map, throwError } from 'rxjs';
import { Cacheable } from 'ts-cacheable';

import { ApiService } from '../../core/api';
import { ResponseDatas } from '../../core/api/types';
import { GeolocationCoords, GeolocationService } from '../geolocation';

import { Country, CountryPhoneConfig } from './types';

@Injectable({
  providedIn: 'root'
})
export class CountriesService {
  private readonly apiService = inject(ApiService);
  private readonly geolocationService = inject(GeolocationService);

  @Cacheable()
  getAll(): Observable<Country[]> {
    return this.apiService.get<ResponseDatas<Country[]>>('Countries/GetAll').pipe(map((result) => result?.datas));
  }

  @Cacheable()
  getCountriesWithSMSSupport(): Observable<CountryPhoneConfig[]> {
    return this.apiService.getUnsecure<CountryPhoneConfig[]>('Countries/login-with-phone-supported');
  }

  getCountry(position: GeolocationCoords): Observable<Country | undefined> {
    return forkJoin({
      addresses: this.geolocationService.lookupAddress(position),
      countries: this.getAll()
    }).pipe(
      map(({ addresses, countries }) => {
        const countryNames = addresses
          .map(
            (address) => address.address_components.find((component) => component.types.includes('country'))?.short_name
          )
          .filter((value) => value);

        const country = countries.find(({ alpha2CountryCode }) => countryNames.includes(alpha2CountryCode));

        if (!country) {
          throwError(() => new Error('countryNotFound'));
        }

        return country;
      })
    );
  }
}
